import React, { useRef, useState } from 'react';
import { Combobox, Listbox, Transition } from '@headlessui/react';
import { Controller } from 'react-hook-form';
import { Control } from 'react-hook-form';
import { FieldErrors } from 'react-hook-form';
import CheckIcon from 'svg/CheckIcon';
import ChevronDown from 'svg/ChevronDown';
import classNames from 'styles/utils/classNames';

type Option = { value: string; label: string };

type Props = {
  label?: string;
  name: string;
  errors: FieldErrors<any>;
  control: Control<any>;
  placeholder: string;
  options: Option[];
  disabled?: boolean;
  searchable?: boolean;
  /** Fires after the form value updates — for dependent-field side effects. */
  onChange?: (value: string) => void;
};

function filterOptions(options: Option[], query: string): Option[] {
  if (!query.trim()) return options;
  const lower = query.toLowerCase();
  return options.filter((o) => o.label.toLowerCase().includes(lower));
}

export default function Select({
  control,
  label,
  name,
  errors,
  options,
  placeholder,
  disabled = false,
  searchable = false,
  onChange: onChangeProp,
}: Props) {
  return (
    <div className="flex flex-col">
      {label && (
        <span className="mb-2 font-bold text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {label}
        </span>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => {
          if (searchable) {
            return (
              <SearchableSelectInner
                value={value}
                onChange={(next) => {
                  onChange(next);
                  onChangeProp?.(next);
                }}
                options={options}
                placeholder={placeholder}
                disabled={disabled}
              />
            );
          }
          return (
            <div className="w-full">
              <Listbox
                value={value}
                onChange={(next) => {
                  onChange(next);
                  onChangeProp?.(next);
                }}
                disabled={disabled}
              >
                {({ open }) => (
                  <>
                    <div className="relative">
                      <Listbox.Button
                        className={classNames(
                          'input-form relative h-11 w-full text-left',
                          disabled && 'cursor-not-allowed opacity-60',
                        )}
                      >
                        <span className="block">
                          {options.find((o) => o.value === value)?.label || placeholder}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ChevronDown className="h-4 text-gray-400" aria-hidden="true" />
                        </span>
                      </Listbox.Button>

                      <Transition
                        show={open}
                        as={React.Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-color-bg-lightmode-primary py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-color-bg-darkmode-primary sm:text-sm">
                          {options.map(({ value, label }) => (
                            <Listbox.Option
                              key={value}
                              className={({ active }) =>
                                classNames(
                                  active ? 'bg-color-bg-lightmode-invert text-color-text-lightmode-invert' : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                                  'relative cursor-pointer select-none px-4 py-2',
                                )
                              }
                              value={value}
                            >
                              {({ selected, active }) => (
                                <>
                                  <span className="block">{label}</span>

                                  {selected ? (
                                    <span
                                      className={classNames(
                                        active ? 'text-color-text-lightmode-invert' : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                                        'absolute inset-y-0 right-0 flex items-center pr-4',
                                      )}
                                    >
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </>
                )}
              </Listbox>
            </div>
          );
        }}
      />
      <p className="bottom-1 mt-1 text-[0.75rem] text-color-error">
        {errors?.[name]?.message as any}
      </p>
    </div>
  );
}

type SearchableSelectInnerProps = {
  value: string | null | undefined;
  onChange: (value: string) => void;
  options: Option[];
  placeholder: string;
  disabled?: boolean;
};

function SearchableSelectInner({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}: SearchableSelectInnerProps) {
  const [query, setQuery] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isDropdownOpen = useRef(false);
  const selectedOption = options.find((o) => o.value === value) ?? null;
  const filteredOptions = filterOptions(options, query);

  return (
    <div className="w-full">
      <Combobox
        as="div"
        value={selectedOption}
        onChange={(option: Option | null) => {
          onChange(option?.value ?? '');
        }}
        disabled={disabled}
      >
        <div className="relative">
          <Combobox.Input
            className={classNames(
              'input-form h-11 w-full pr-10',
              disabled && 'cursor-not-allowed opacity-60',
            )}
            placeholder={placeholder}
            displayValue={(option: Option | null) => option?.label ?? ''}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              if (!isDropdownOpen.current) {
                buttonRef.current?.click();
              }
            }}
          />
          <Combobox.Button
            ref={buttonRef}
            className="absolute inset-y-0 right-0 flex items-center pr-3"
          >
            <ChevronDown className="h-4 text-gray-400" aria-hidden="true" />
          </Combobox.Button>

          <Transition
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            beforeEnter={() => {
              isDropdownOpen.current = true;
            }}
            afterLeave={() => {
              isDropdownOpen.current = false;
              setQuery('');
            }}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-color-bg-lightmode-primary py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-color-bg-darkmode-primary sm:text-sm">
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-2 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  No matches
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option.value}
                    className={({ active }) =>
                      classNames(
                        active ? 'bg-color-bg-lightmode-invert text-color-text-lightmode-invert' : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                        'relative cursor-pointer select-none px-4 py-2',
                      )
                    }
                    value={option}
                  >
                    {({ selected, active }) => (
                      <>
                        <span className="block truncate">{option.label}</span>
                        {selected ? (
                          <span
                            className={classNames(
                              active ? 'text-color-text-lightmode-invert' : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                              'absolute inset-y-0 right-0 flex items-center pr-4',
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
